import axios, { AxiosError, ResponseType } from 'axios';
import { HttpException, UnauthorizedException } from './exceptions';

const DEFAULT_TIMEOUT_MS = 15000;

export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  responseType?: ResponseType;
  withCredentials?: boolean;
  signal?: AbortSignal;
  timeout?: number;
  onUploadProgress?: (loaded: number, total: number) => void;
}

export interface HttpResponse<T> {
  data: T;
  status: number;
}

export interface HttpClientProviders<TBaseUrl extends string | undefined = string> {
  getBaseUrl: () => TBaseUrl;
  getAuthHeader?: () => Record<string, string> | undefined;
  onUnauthorized?: () => void;
}

type InternalOptions = RequestOptions & { method: string; data?: unknown };

function isFormData(data: unknown): boolean {
  return typeof FormData !== 'undefined' && data instanceof FormData;
}

export class HttpClient<TBaseUrl extends string | undefined = string> {
  constructor(private readonly providers: HttpClientProviders<TBaseUrl>) {}

  private buildUrl(endpoint: string): string {
    const baseUrl = this.providers.getBaseUrl();

    if (!baseUrl) {
      throw new Error(`[sycai] HttpClient has no base URL for "${endpoint}"`);
    }

    return `${baseUrl.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;
  }

  private async request<T>(endpoint: string, options: InternalOptions): Promise<HttpResponse<T>> {
    const {
      method,
      data,
      headers,
      params,
      responseType,
      withCredentials = true,
      signal,
      timeout,
      onUploadProgress,
    } = options;

    const url = this.buildUrl(endpoint);

    try {
      const response = await axios({
        url,
        method,
        data,
        params,
        responseType,
        withCredentials,
        signal,
        timeout: timeout ?? DEFAULT_TIMEOUT_MS,
        onUploadProgress: onUploadProgress
          ? event => onUploadProgress(event.loaded, event.total ?? 0)
          : undefined,
        headers: {
          ...(data === undefined || isFormData(data) ? {} : { 'Content-Type': 'application/json' }),
          ...(this.providers.getAuthHeader?.() ?? {}),
          ...headers,
        },
      });

      return { data: response.data, status: response.status };
    } catch (error) {
      const exception = this.toException(error as AxiosError);

      if (exception instanceof UnauthorizedException) {
        this.providers.onUnauthorized?.();
      }

      throw exception;
    }
  }

  private toException(error: AxiosError): HttpException {
    const response = error.response;

    if (!response) {
      return new HttpException(0, error.message || 'Network error');
    }

    const body = response.data;
    const message =
      (body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string'
        ? (body as { message: string }).message
        : undefined) ??
      error.message ??
      'Unknown error occurred';

    if (response.status === 401 || UnauthorizedException.is(body)) {
      return new UnauthorizedException(message);
    }

    return new HttpException(response.status, message);
  }

  public get<T>(endpoint: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  public post<T>(endpoint: string, data?: unknown, options: RequestOptions = {}) {
    return this.request<T>(endpoint, { ...options, method: 'POST', data });
  }

  public put<T>(endpoint: string, data?: unknown, options: RequestOptions = {}) {
    return this.request<T>(endpoint, { ...options, method: 'PUT', data });
  }

  public patch<T>(endpoint: string, data?: unknown, options: RequestOptions = {}) {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', data });
  }

  public delete<T>(endpoint: string, options: RequestOptions = {}): Promise<HttpResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}
